// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from "react";
import type { RestEndpointMeta } from "./types";
import type { SampleContext } from "./codegen/curl";
import { curlSample } from "./codegen/curl";
import { goSample } from "./codegen/go";
import { pythonSample } from "./codegen/python";
import { tsSample } from "./codegen/typescript";

/** Tab identifiers for the code-sample strip. */
type SampleTab = "curl" | "go" | "python" | "typescript";

const TABS: Array<{ id: SampleTab; label: string }> = [
  { id: "curl",       label: "curl" },
  { id: "go",         label: "Go" },
  { id: "python",     label: "Python" },
  { id: "typescript", label: "TypeScript" },
];

/**
 * CodeSamples renders a four-tab code-sample strip for a single REST endpoint.
 *
 * Props:
 *   endpoint  - the RestEndpointMeta to generate samples for.
 *   ctx       - runtime context (baseUrl, pathParams, body, token, apiKey).
 *   className - extra CSS class appended to the root element.
 *
 * The active tab defaults to "curl". Switching tabs calls the matching
 * pure builder function and renders the result in a <pre> block.  The active
 * sample string is memoized on [activeTab, endpoint, ctx] so the builder
 * functions are only called when the relevant inputs change.
 *
 * The copy-to-clipboard button is guarded by a capability check so the
 * component is safe to import and render on React Native / SSR environments
 * that don't provide navigator.clipboard.
 *
 * No web-global dependencies at module load time (RN-safe).
 */
export const CodeSamples = React.memo(function CodeSamples(props: {
  endpoint: RestEndpointMeta;
  ctx: SampleContext;
  className?: string;
}): JSX.Element {
  const { endpoint, ctx, className } = props;
  const [activeTab, setActiveTab] = useState<SampleTab>("curl");
  const [copied, setCopied] = useState<boolean>(false);

  const root = ["apic-ui-code-samples", className].filter(Boolean).join(" ");

  // Memoize the active sample so builder functions are only called when the
  // tab, endpoint, or context changes — not on every parent re-render.
  const sample = useMemo((): string => {
    switch (activeTab) {
      case "curl":       return curlSample(endpoint, ctx);
      case "go":         return goSample(endpoint, ctx);
      case "python":     return pythonSample(endpoint, ctx);
      case "typescript": return tsSample(endpoint, ctx);
    }
  }, [activeTab, endpoint, ctx]);

  function handleCopy(): void {
    // Guard: navigator.clipboard is only available in secure browser contexts.
    // This check keeps the component safe in RN / SSR environments.
    // We access navigator and setTimeout via globalThis so this module is
    // import-safe without DOM lib (no web globals at module load time).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis as any;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const nav = g.navigator as { clipboard?: { writeText?: (s: string) => Promise<void> } } | undefined;
    if (nav !== undefined && nav.clipboard !== undefined && typeof nav.clipboard.writeText === "function") {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      nav.clipboard.writeText(sample).then(
        () => {
          setCopied(true);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          (g.setTimeout as ((fn: () => void, ms: number) => void))(() => { setCopied(false); }, 1500);
        },
        () => { /* clipboard write rejected — ignore silently */ },
      );
    }
  }

  return (
    <div className={root}>
      {/* Tab strip */}
      <div className="apic-ui-code-samples-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={
              "apic-ui-code-tab" +
              (activeTab === tab.id ? " apic-ui-code-tab--active" : "")
            }
            onClick={() => { setActiveTab(tab.id); setCopied(false); }}
          >
            {tab.label}
          </button>
        ))}

        {/* Copy button — rendered only when clipboard is available (SSR: always rendered
            but click-guarded). Placed inside the tab strip for layout convenience. */}
        <button
          className={"apic-ui-code-copy" + (copied ? " apic-ui-code-copy--copied" : "")}
          aria-label={copied ? "Copied!" : "Copy to clipboard"}
          onClick={handleCopy}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>

      {/* Code panel */}
      <pre className={"apic-ui-code-panel apic-ui-code-panel--" + activeTab}>
        <code>{sample}</code>
      </pre>
    </div>
  );
});
